import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSun,
  faCloud,
  faCloudRain,
  faSnowflake,
  faBolt,
  faSmog,
  faCloudSun,
  faMoon,
} from "@fortawesome/free-solid-svg-icons";
import { Context } from "../App";
import { PanelHeader } from "./Panel";
import { ForecastHour } from "../Contract/Weather";

// Map condition text to Font Awesome icons.
function getWeatherIcon(conditionText: string, isDay = true) {
  const txt = conditionText.toLowerCase();
  if (txt.includes("thunder") || txt.includes("storm")) return faBolt;
  if (txt.includes("snow") || txt.includes("sleet")) return faSnowflake;
  if (txt.includes("rain") || txt.includes("drizzle") || txt.includes("shower"))
    return faCloudRain;
  if (txt.includes("partly")) return isDay ? faCloudSun : faCloud;
  if (txt.includes("cloud") || txt.includes("overcast")) return faCloud;
  if (txt.includes("sun") || txt.includes("clear"))
    return isDay ? faSun : faMoon;
  return faSmog;
}

// "07:21 AM" -> "07:21", "06:14 PM" -> "18:14"
function to24h(t: string) {
  const m = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return t;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

const CHART_W = 300;
const CHART_H = 64;

/** Next 24 hours of temperature as a line, with rain chance as bars. */
function HourlyChart({ hours }: { hours: ForecastHour[] }) {
  const { line, nearLine, bars, min, max } = useMemo(() => {
    const temps = hours.map((h) => h.temp_c);
    const min = Math.floor(Math.min(...temps));
    const max = Math.ceil(Math.max(...temps));
    const span = Math.max(max - min, 1);
    const step = CHART_W / Math.max(hours.length - 1, 1);
    const y = (t: number) => 6 + (1 - (t - min) / span) * (CHART_H - 22);
    const pts = hours.map((h, i) => `${(i * step).toFixed(1)},${y(h.temp_c).toFixed(1)}`);
    const bars = hours.map((h, i) => ({
      x: i * step - step * 0.2,
      w: step * 0.4,
      h: (h.chance_of_rain / 100) * 14,
    }));
    return {
      line: pts.join(" "),
      nearLine: pts.slice(0, 4).join(" "),
      bars,
      min,
      max,
    };
  }, [hours]);

  return (
    <div className="wx-chart">
      <div className="wx-chart__plot">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none">
          {[0.33, 0.66].map((f) => (
            <line
              key={f}
              x1={0}
              x2={CHART_W}
              y1={CHART_H * f}
              y2={CHART_H * f}
              className="wx-chart__grid"
            />
          ))}
          {bars.map((b, i) =>
            b.h > 0 ? (
              <rect
                key={i}
                x={b.x}
                y={CHART_H - b.h}
                width={b.w}
                height={b.h}
                className="wx-chart__rain"
              />
            ) : null,
          )}
          <polyline points={line} className="wx-chart__line" />
          <polyline points={nearLine} className="wx-chart__near" />
        </svg>
        <span className="wx-chart__max">{max}°</span>
        <span className="wx-chart__min">{min}°</span>
      </div>
      <div className="wx-chart__axis">
        <span className="is-accent">Now</span>
        <span>+6h</span>
        <span>+12h</span>
        <span>+18h</span>
        <span>+24h</span>
      </div>
    </div>
  );
}

export const WeatherComponent = () => {
  const { weather } = React.useContext(Context);
  const { location, current, forecast } = weather!;
  const today = forecast.forecastday[0];

  // The next 24 hourly readings, starting with the current hour.
  const next24 = useMemo(() => {
    const nowSec = Date.now() / 1000 - 3600;
    return forecast.forecastday
      .flatMap((d) => d.hour ?? [])
      .filter((h) => h.time_epoch >= nowSec)
      .slice(0, 25);
  }, [forecast]);

  const stats: Array<[string, string]> = [
    ["Feels", `${Math.round(current.feelslike_c)}°`],
    ["Wind", `${Math.round(current.wind_mph)} mph ${current.wind_dir}`],
    ["Humidity", `${current.humidity}%`],
    ["Rain", `${today?.day.daily_chance_of_rain ?? 0}%`],
    ["Sunrise", today ? to24h(today.astro.sunrise) : "—"],
    ["Sunset", today ? to24h(today.astro.sunset) : "—"],
  ];

  return (
    <div className="wx">
      <PanelHeader label="Weather" meta={location.name} />

      <div className="wx__now">
        <div className="wx__temp">
          {Math.round(current.temp_c)}
          <span>°C</span>
        </div>
        <div className="wx__cond">
          <FontAwesomeIcon
            icon={getWeatherIcon(current.condition.text, current.is_day === 1)}
            className="wx__icon"
          />
          <span>{current.condition.text}</span>
        </div>
      </div>

      <dl className="wx__stats">
        {stats.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>

      {next24.length > 2 && <HourlyChart hours={next24} />}

      <div className="wx__forecast">
        {forecast.forecastday.map((day, i) => {
          const d = new Date(day.date);
          const name =
            i === 0
              ? "Today"
              : d.toLocaleDateString("en-GB", { weekday: "short" });
          return (
            <div key={day.date} className={`wx__day ${i === 0 ? "is-today" : ""}`}>
              <span className="wx__dayname">{name}</span>
              <FontAwesomeIcon
                icon={getWeatherIcon(day.day.condition.text)}
                className="wx__dayicon"
              />
              <span className="wx__range">
                {Math.round(day.day.maxtemp_c)}°
                <em> / {Math.round(day.day.mintemp_c)}°</em>
              </span>
              <span className="wx__rain">
                {day.day.daily_chance_of_rain > 0
                  ? `${day.day.daily_chance_of_rain}%`
                  : "—"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
